import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex font-semibold text-sm items-center justify-center whitespace-nowrap rounded-md transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-white px-6 hover:bg-primaryDark',
        destructive: 'bg-[#FF3A28] text-white hover:bg-[#FF3A28]/90',
        outline:
          'border border-[#CCCCCC] hover:bg-zinc-100 bg-white text-[#555555]',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-[#F6F6F6] text-primary',
        link: 'text-primary underline-offset-4 hover:underline',
        bare: '',
      },
      size: {
        default: 'h-10 px-4 rounded-md py-2 text-base',
        sm: 'h-8 rounded-md px-4 py-1 text-sm',
        lg: 'h-10 rounded-md text-sm font-medium px-4',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
